import reduce from 'lodash/reduce'
import forEach from 'lodash/forEach'
import isNaN from 'lodash/isNaN'
import { Vector, Line, Ellipse, Box } from './geometry'

// Canvas coords: x right, y down — matches atan2(dy, dx) used elsewhere in this file
const DIRECTION_ANGLES: Record<'ltr' | 'rtl' | 'ttb' | 'btt', number> = {
  ltr: 0,
  rtl: Math.PI,
  ttb: Math.PI / 2,
  btt: -Math.PI / 2,
}

type SimulationSettings = {
  stepsPerFrame: number
  convergeSpeed: number
  repulsion: number
  wallForce: number
  centerAttraction: number
  springLength: number
  stiffness: number
  scaler: number
  damping: number
  speedLimit: number
  focusModifier: number
  angularStiffness: number
}

type DragState = {
  isDragging: boolean
  id: string | null
}

type SimulationDimensions = {
  center: any
  window: any
  box: any
  mouse: any
  mouseOffset: any
  drag: DragState
}

type CreatingState = {
  isCreating: boolean
  ids: string[]
}

type SimPoint = {
  id: string
  width: number
  height: number
  depth: number
  content?: string
  focused?: boolean
  isMore?: boolean
  modified: number
  position: any
  velocity: any
  force: any
  zoom: number
  getPosition: () => any
  getLineToEllipse: (p: SimPoint) => any
  getLineToNode: (p: SimPoint, displace?: number) => any
  getBox: () => any
  applyForce: (f: any) => void
  setStyle: (style: Record<string, string>) => void
  setZoom: (zoom: number) => void
  getZoom: (max: any) => number
  getColor: () => string
  getLineWidth: () => number
}

type SimEdge = SimPoint & {
  from: string
  to: string
  direction?: 'free' | 'ltr' | 'rtl' | 'ttb' | 'btt'
  directionSoft?: boolean
  spring?: boolean
  stiffness?: number
  springLength?: number
  hasVisibleLabel: () => boolean
}

type SimulationGraph = {
  nodes: Record<string, SimPoint>
  edges: Record<string, SimEdge>
  modified: number
}

type ShapeLayout = {
  ids: string[]
  targets: Record<string, { x: number; y: number }>
  settings: { direction: string; wrap: boolean; showInterItemEdges: boolean }
}

type ShapesState = Record<string, ShapeLayout>

export const Simulation = class Simulation extends EventTarget {
  running: boolean
  simulating: boolean
  _startedAt: number
  settings: SimulationSettings
  graph: SimulationGraph
  dimensions: SimulationDimensions
  creating: CreatingState
  shapes: ShapesState

  constructor(settings: SimulationSettings, graph: SimulationGraph, dimensions: SimulationDimensions, creating: CreatingState, shapes: ShapesState) {
    super()
    this.settings = settings
    this.graph = graph
    this.dimensions = dimensions
    this.creating = creating
    this.shapes = shapes
    this.running = false
    this.simulating = false
    this._startedAt = 0
  }

  start() {
    this.simulating = true
    if (this.running) return
    this.running = true
    this._startedAt = Date.now()
    const doFrame = () => {
      if (!this.running) return
      this.dispatchEvent(new Event('preframe'))
      if (this.simulating) {
        for (let i = 0; i < this.settings.stepsPerFrame; i += 1) {
          this.step()
        }
        this.updateAllZoom()
      }
      this.dispatchEvent(new Event('render'))
      requestAnimationFrame(doFrame)
    }
    requestAnimationFrame(doFrame)
  }

  stop() {
    this.simulating = false
    this.dispatchEvent(new Event('stop'))
  }

  _getShapedTargets(): Record<string, { x: number; y: number }> {
    const targets: Record<string, { x: number; y: number }> = {}
    for (const shape of Object.values(this.shapes) as ShapeLayout[]) {
      Object.assign(targets, shape.targets)
    }
    return targets
  }

  converged() {
    if (this.dimensions.drag.isDragging) return false
    const shapedTargets = this._getShapedTargets()
    const hasShapes = Object.keys(shapedTargets).length > 0
    const maxVel = reduce(this.graph.nodes, (max: number, n: SimPoint) => {
      if (n.focused) return max
      if (hasShapes && shapedTargets[n.id]) {
        // Shaped members don't carry real physics velocity; measure their remaining
        // ease-toward-target motion instead (same multiplier as overrideShapeVelocity's
        // non-dragging case), so convergence waits for them to actually arrive.
        const target = shapedTargets[n.id]!
        const dx = (target.x - n.position.x) / this.settings.scaler * 0.1
        const dy = (target.y - n.position.y) / this.settings.scaler * 0.1
        const v = Math.sqrt(dx * dx + dy * dy)
        return v > max ? v : max
      }
      const v = n.velocity.magnitude()
      return v > max ? v : max
    }, 0)
    return maxVel < this.settings.convergeSpeed
  }

  step() {
    this.applyHookesLaw()
    this.applyDirectionalConstraint()
    this.applyCoulombsLaw()
    this.attractToCenter()
    this.updateAllVelocity()
    // Save physics-only velocities before position corrections
    const savedVelocities: Record<string, { x: number, y: number }> = {}
    forEach(this.graph.nodes, (n: SimPoint) => {
      savedVelocities[n.id] = { x: n.velocity.x, y: n.velocity.y }
    })
    this.separateOverlaps()
    this.separateFromShapeBounds()
    this.overrideDraggingVelocity()
    this.overrideShapeVelocity()
    this.preventWallOverlap()
    if (Date.now() - this._startedAt >= 500 && this.converged()) {
      this.stop()
      return
    }
    this.updateAllPosition()
    // Restore physics-only velocities so impulses don't persist
    forEach(this.graph.nodes, (n: SimPoint) => {
      const saved = savedVelocities[n.id]
      if (saved) {
        n.velocity.x = saved.x
        n.velocity.y = saved.y
      }
    })
  }

  separateOverlaps() {
    const nodes = Object.values(this.graph.nodes)
    const shapedIds = this._getShapedTargets()
    const directLineIds = this._getDirectLineIds()
    const invScaler = 1 / this.settings.scaler
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i]
        const b = nodes[j]
        if (!a || !b) continue
        if (directLineIds.has(a.id) || directLineIds.has(b.id)) continue
        // Skip shaped-vs-non-shaped pairs; bounding box handles those
        const aInShape = shapedIds[a.id] != null
        const bInShape = shapedIds[b.id] != null
        if (aInShape !== bInShape) continue
        const ac = a.getPosition()
        const bc = b.getPosition()
        const dx = ac.x - bc.x
        const dy = ac.y - bc.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        const ang = Math.atan2(dy, dx)
        const aEdge = this.boxEdgeDist(a, ang)
        const bEdge = this.boxEdgeDist(b, ang + Math.PI)
        const minDist = aEdge + bEdge + 10
        const overlap = minDist - dist

        if (overlap <= 1) continue
        if (a.focused && b.focused) continue

        const nudge = overlap * 0.5
        let nx = dx
        let ny = dy
        if (dist > 0) {
          nx /= dist
          ny /= dist
        } else {
          nx = 1
          ny = 0
        }
        // If only one can move, it gets the full nudge; if both, each gets half
        const share = (a.focused || b.focused) ? 1.0 : 0.5
        const vx = nx * nudge * share * invScaler
        const vy = ny * nudge * share * invScaler
        if (!a.focused) {
          a.velocity.x += vx
          a.velocity.y += vy
        }
        if (!b.focused) {
          b.velocity.x -= vx
          b.velocity.y -= vy
        }
      }
    }
  }

  separateFromShapeBounds() {
    const shapedIds = this._getShapedTargets()
    const invScaler = 1 / this.settings.scaler
    const pad = 20

    const shapeBounds: Array<{ left: number; top: number; right: number; bottom: number }> = []
    for (const shape of Object.values(this.shapes) as ShapeLayout[]) {
      let left = Infinity
      let top = Infinity
      let right = -Infinity
      let bottom = -Infinity
      for (const id of shape.ids) {
        const n = this.graph.nodes[id]
        if (!n) continue
        left = Math.min(left, n.position.x)
        top = Math.min(top, n.position.y)
        right = Math.max(right, n.position.x + n.width)
        bottom = Math.max(bottom, n.position.y + n.height)
      }
      if (left === Infinity) continue
      shapeBounds.push({ left: left - pad, top: top - pad, right: right + pad, bottom: bottom + pad })
    }

    if (shapeBounds.length === 0) return

    const directLineIds = this._getDirectLineIds()
    forEach(this.graph.nodes, (n: SimPoint) => {
      if (shapedIds[n.id] != null) return
      if (directLineIds.has(n.id)) return
      if (n.focused) return
      for (const b of shapeBounds) {
        const nLeft = n.position.x
        const nTop = n.position.y
        const nRight = nLeft + n.width
        const nBottom = nTop + n.height
        if (nRight <= b.left || nLeft >= b.right || nBottom <= b.top || nTop >= b.bottom) continue

        // Disqualify exit directions where the node would be trapped
        // between the shape bounds and a viewport wall
        const boxW = this.dimensions.box.width
        const boxH = this.dimensions.box.height
        const pushLeft  = b.left >= n.width  ? (nRight - b.left)  : Infinity
        const pushRight = (boxW - b.right) >= n.width  ? (b.right - nLeft)  : Infinity
        const pushUp    = b.top >= n.height  ? (nBottom - b.top)  : Infinity
        const pushDown  = (boxH - b.bottom) >= n.height ? (b.bottom - nTop) : Infinity
        const minPush = Math.min(pushLeft, pushRight, pushUp, pushDown)
        if (!isFinite(minPush)) continue

        let vx = 0
        let vy = 0
        if (minPush === pushLeft) vx = -pushLeft
        else if (minPush === pushRight) vx = pushRight
        else if (minPush === pushUp) vy = -pushUp
        else vy = pushDown

        n.velocity.x += vx * 0.5 * invScaler
        n.velocity.y += vy * 0.5 * invScaler
      }
    })
  }

  _getDirectLineIds(): Set<string> {
    const ids = new Set<string>()
    forEach(this.graph.edges, (e: SimEdge) => {
      if (!e.hasVisibleLabel()) ids.add(e.id)
    })
    return ids
  }

  _pairKey(a: string, b: string): string {
    return a < b ? `${a}|${b}` : `${b}|${a}`
  }

  // Pairs directly joined by a spring in applyHookesLaw, so repulsion doesn't fight the spring.
  _getSpringPairs(): Set<string> {
    const pairs = new Set<string>()
    forEach(this.graph.edges, (e: SimEdge) => {
      if (e.hasVisibleLabel()) {
        pairs.add(this._pairKey(e.from, e.id))
        pairs.add(this._pairKey(e.to, e.id))
      } else if (e.spring !== false) {
        pairs.add(this._pairKey(e.from, e.to))
      }
    })
    return pairs
  }

  attractToCenter() {
    const directLineIds = this._getDirectLineIds()
    forEach(this.graph.nodes, (n: SimPoint) => {
      if (directLineIds.has(n.id)) return
      this.attractToPoint({ [`${n.id}`]: n }, this.dimensions.center, this.settings.centerAttraction)
    })
  }

  attractToPoint(nodes: Record<string, SimPoint>, point: any, attraction: number) {
    forEach(nodes, (n: SimPoint) => {
      const p = n.getPosition()
      const dist = point.distance(p)
      const multi = dist * attraction
      const direc = point.sub(p).normalize_m().mul_m(multi)
      this.applyForce(n, direc)
    })
  }

  applyHookesLaw() {
    const shapedTargets = this._getShapedTargets()
    forEach(this.graph.edges, (e: SimEdge) => {
      const springLength = e.springLength ?? this.settings.springLength
      // Labeled edges are drawn as two segments, so each should target half the length
      const halfSpringLength = springLength / 2
      const stiffness = e.stiffness ?? this.settings.stiffness
      const fromShaped = shapedTargets[e.from] != null
      const toShaped = shapedTargets[e.to] != null
      // Skip spring forces when both endpoints are shaped, unless the edge
      // has a visible label that needs positioning
      if (fromShaped && toShaped) {
        if (!e.hasVisibleLabel()) return
        // Only apply force to the edge label, not to shaped endpoints
        this.hookesLawOneWay(this.graph.nodes[e.from], e, halfSpringLength, stiffness)
        this.hookesLawOneWay(this.graph.nodes[e.to], e, halfSpringLength, stiffness)
        return
      }
      if (e.spring === false) {
        // "Pulls together" disabled: don't pull the endpoints toward each other,
        // but still keep a visible label positioned between them
        if (!e.hasVisibleLabel()) return
        this.hookesLawOneWay(this.graph.nodes[e.from], e, halfSpringLength, stiffness)
        this.hookesLawOneWay(this.graph.nodes[e.to], e, halfSpringLength, stiffness)
        return
      }
      if (e.hasVisibleLabel()) {
        // Two half-springs: FROM↔edge label, edge label↔TO
        this.hookesLaw(this.graph.nodes[e.from], e, halfSpringLength, stiffness)
        this.hookesLaw(this.graph.nodes[e.to], e, halfSpringLength, stiffness)
      } else {
        // Single spring directly between endpoints
        this.hookesLaw(this.graph.nodes[e.from], this.graph.nodes[e.to], springLength, stiffness)
      }
    })
  }

  // Torque that rotates two points toward a target angle, independent of the radial
  // spring above. 'soft' directions only push back once outside a 90° cone.
  applyDirectionalConstraint() {
    forEach(this.graph.edges, (e: SimEdge) => {
      if (!e.direction || e.direction === 'free') return
      const from = this.graph.nodes[e.from]
      const to = this.graph.nodes[e.to]
      if (!from || !to || from === to) return

      if (e.hasVisibleLabel()) {
        // Two half-torques: FROM↔edge label, edge label↔TO
        this.directionalTorque(from, e, e.direction, e.directionSoft)
        this.directionalTorque(e, to, e.direction, e.directionSoft)
      } else {
        this.directionalTorque(from, to, e.direction, e.directionSoft)
      }
    })
  }

  directionalTorque(f: SimPoint, t: SimPoint, direction: 'ltr' | 'rtl' | 'ttb' | 'btt', soft?: boolean) {
    const fp = f.getPosition()
    const tp = t.getPosition()
    const d = tp.sub(fp)
    const dist = d.magnitude()
    if (dist === 0) return

    const targetAngle = DIRECTION_ANGLES[direction]
    const currentAngle = Math.atan2(d.y, d.x)
    const diff = Math.atan2(Math.sin(currentAngle - targetAngle), Math.cos(currentAngle - targetAngle))

    const halfWidth = soft ? Math.PI / 4 : 0
    if (Math.abs(diff) <= halfWidth) return

    const overshoot = diff - Math.sign(diff) * halfWidth
    const ux = d.x / dist
    const uy = d.y / dist
    // 90° CCW rotation of the edge's unit direction vector
    const perpX = -uy
    const perpY = ux
    const gain = this.settings.angularStiffness * Math.sin(overshoot) * -1
    const fx = perpX * gain
    const fy = perpY * gain
    this.applyForce(f, { x: -fx, y: -fy })
    this.applyForce(t, { x: fx, y: fy })
  }

  hookesLaw(f: SimPoint | undefined, t: SimPoint | undefined, springLength = this.settings.springLength, stiffness = this.settings.stiffness) {
    if (!(f && t)) return
    const fp = f.getPosition()
    const tp = t.getPosition()
    const d = tp.sub(fp)
    const centerDist = d.magnitude()
    if (centerDist === 0) return
    d.normalize_m()
    const ang = Math.atan2(d.y, d.x)
    const fromEdge = this.boxEdgeDist(f, ang)
    const toEdge = this.boxEdgeDist(t, ang + Math.PI)
    const mag = centerDist - fromEdge - toEdge
    const displacement = springLength - mag
    d.mul_m(stiffness * displacement * -0.5)
    f.applyForce(d)
    t.applyForce(d.neg_m())
  }

  // Like hookesLaw but only applies force to t (the second argument).
  // Used so intra-shape edge labels get positioned without pushing shaped endpoints.
  hookesLawOneWay(f: SimPoint | undefined, t: SimPoint | undefined, springLength = this.settings.springLength, stiffness = this.settings.stiffness) {
    if (!(f && t)) return
    const fp = f.getPosition()
    const tp = t.getPosition()
    const d = tp.sub(fp)
    const centerDist = d.magnitude()
    if (centerDist === 0) return
    d.normalize_m()
    const ang = Math.atan2(d.y, d.x)
    const fromEdge = this.boxEdgeDist(f, ang)
    const toEdge = this.boxEdgeDist(t, ang + Math.PI)
    const mag = centerDist - fromEdge - toEdge
    const displacement = springLength - mag
    d.mul_m(stiffness * displacement * -0.5)
    t.applyForce(d.neg_m())
  }

  boxEdgeDist(n: SimPoint, angle: number) {
    const hw = n.width / 2
    const hh = n.height / 2
    const cosA = Math.abs(Math.cos(angle))
    const sinA = Math.abs(Math.sin(angle))
    if (cosA < 1e-10) return hh
    if (sinA < 1e-10) return hw
    return Math.min(hw / cosA, hh / sinA)
  }

  applyCoulombsLaw() {
    const shapedTargets = this._getShapedTargets()
    const directLineIds = this._getDirectLineIds()
    const springPairs = this._getSpringPairs()
    forEach(this.graph.nodes, (n1: SimPoint) => {
      if (directLineIds.has(n1.id)) return
      forEach(this.graph.nodes, (n2: SimPoint) => {
        if (n1 === n2) return
        if (directLineIds.has(n2.id)) return
        // Skip repulsion between two shaped nodes (internal to shape system)
        if (shapedTargets[n1.id] != null && shapedTargets[n2.id] != null) return
        // Skip repulsion between objects directly connected by a spring
        if (springPairs.has(this._pairKey(n1.id, n2.id))) return
        const d = this.coulombsLaw(n1, n2)
        n1.applyForce(d)
        n2.applyForce(d.neg_m())
      })
    })
  }

  coulombsLaw(n1: SimPoint, n2: SimPoint) {
    const p1 = n1.getPosition()
    const p2 = n2.getPosition()
    const d = p1.sub(p2)
    const centerDist = d.magnitude()
    if (centerDist === 0) {
      d.x = 50
      d.y = 50
      return d
    }
    d.normalize_m()
    const ang = Math.atan2(d.y, d.x)
    const fromEdge = this.boxEdgeDist(n1, ang)
    const toEdge = this.boxEdgeDist(n2, ang + Math.PI)
    const distance = Math.max((centerDist - fromEdge - toEdge) / 100.0, 1)
    const strength = this.settings.repulsion / (Math.pow(distance, 1.5) * 0.5)
    return d.mul_m(strength)
  }

  applyForce(o: SimPoint, force: any) {
    if (isNaN(force.x)) force.x = 0
    if (isNaN(force.y)) force.y = 0
    o.applyForce(force)
  }

  updateAllVelocity() {
    forEach(this.graph.nodes, (n: SimPoint) => {
      const oldVx = n.velocity.x
      const oldVy = n.velocity.y
      const f = n.force.mul(this.settings.scaler)
      let damping = this.settings.damping
      const now = Date.now()
      if (now - this.graph.modified < 300 && now - n.modified > 300) {
        damping = 0.05
      }
      n.velocity.add_m(f.x, f.y).mul_m(damping)
      const dot = n.velocity.x * oldVx + n.velocity.y * oldVy
      if (dot < 0) {
        n.velocity.mul_m(0.5)
      }
      const speed = n.velocity.magnitude()
      if (speed > this.settings.speedLimit) {
        n.velocity.normalize_m().mul_m(this.settings.speedLimit)
      }
      if (n.focused) n.velocity.mul_m(this.settings.focusModifier)
      n.force.set_m(0, 0)
    })
  }

  overrideDraggingVelocity() {
    forEach(this.graph.nodes, (n: SimPoint) => {
      if (this.dimensions.drag.id === n.id) {
        const drag = this.getDragPosition()
        n.velocity.x = (drag.x - n.position.x) / this.settings.scaler
        n.velocity.y = (drag.y - n.position.y) / this.settings.scaler
      }
    })
  }

  overrideShapeVelocity() {
    const targets = this._getShapedTargets()
    const dragId = this.dimensions.drag.isDragging ? this.dimensions.drag.id : null
    // Check if the dragged node belongs to any shape
    const dragInShape = dragId != null && targets[dragId] != null

    // Compute per-shape average physics velocity (net external force on the block).
    // Inter-member forces cancel out in the average; what remains is repulsion
    // from non-list nodes pushing the block as a whole.
    const nodeDrift: Record<string, { x: number; y: number }> = {}
    if (!dragInShape) {
      for (const shape of Object.values(this.shapes) as ShapeLayout[]) {
        // Skip drift when any member of this shape is focused
        let hasFocus = false
        let sumVx = 0
        let sumVy = 0
        let count = 0
        for (const id of shape.ids) {
          const n = this.graph.nodes[id]
          if (!n) continue
          if (n.focused) hasFocus = true
          sumVx += n.velocity.x
          sumVy += n.velocity.y
          count++
        }
        if (hasFocus || count === 0) continue
        const drift = { x: sumVx / count, y: sumVy / count }
        for (const id of shape.ids) {
          nodeDrift[id] = drift
        }
      }
    }

    forEach(this.graph.nodes, (n: SimPoint) => {
      const target = targets[n.id]
      if (!target) return
      // Let overrideDraggingVelocity handle the dragged node
      if (n.id === dragId) return
      // Snap rigidly (1.0) when a sibling is being dragged; ease (0.1) otherwise
      const multiplier = dragInShape ? 1.0 : 0.1
      const drift = nodeDrift[n.id]
      n.velocity.x = (target.x - n.position.x) / this.settings.scaler * multiplier + (drift ? drift.x : 0)
      n.velocity.y = (target.y - n.position.y) / this.settings.scaler * multiplier + (drift ? drift.y : 0)
    })
  }

  preventWallOverlap(){
    forEach(this.graph.nodes, (n: SimPoint) => {
      let padX = 0
      let padY = 0
      if (this.dimensions.drag.id === n.id) {
        padX = -n.width/2
        padY = -n.height/2
      }
      const minX = padX
      const maxX = this.dimensions.box.width - n.width - padX
      const minY = padY
      const maxY = this.dimensions.box.height - n.height - padY
      const projX = n.position.x + n.velocity.x * this.settings.scaler
      const projY = n.position.y + n.velocity.y * this.settings.scaler
      if (projX < minX) n.velocity.x = (minX - n.position.x) / this.settings.scaler
      if (projX > maxX && maxX > minX) n.velocity.x = (maxX - n.position.x) / this.settings.scaler
      if (projY < minY) n.velocity.y = (minY - n.position.y) / this.settings.scaler
      if (projY > maxY && maxY > minY) n.velocity.y = (maxY - n.position.y) / this.settings.scaler
    })
  }

  updateAllPosition() {
    forEach(this.graph.nodes, (n: SimPoint) => {
      const v = n.velocity.mul(this.settings.scaler)
      n.position.add_m(v.x, v.y)
    })
  }

  updateAllZoom() {
    const shapedTargetZoom = Math.floor(100 / Math.pow(2, 0.6))
    const shapedIds = this._getShapedTargets()
    forEach(this.graph.nodes, (n: SimPoint) => {
      const max = new Vector(this.dimensions.box.width / 2, this.dimensions.box.height / 2)
      const target = shapedIds[n.id] ? shapedTargetZoom : n.getZoom(max)
      const diff = target - n.zoom
      let step = diff / 4
      if (step < 0) step = Math.ceil(step)
      if (step > 0) step = Math.floor(step)
      n.zoom += step
    })
  }

  getDragPosition() {
    return this.dimensions.mouse.sub(this.dimensions.mouseOffset)
  }
}

type PointAttrs = {
  setStyle: (style: Record<string, string>) => void
  setZoom: (zoom: number) => void
  focused?: boolean
  depth?: number
  position?: any
  content?: string
  modified?: number
  color?: string
  bold?: boolean
  preserved?: boolean
  [key: string]: unknown
}

export const Point = class Point {
  [key: string]: any
  setStyle: (style: Record<string, string>) => void
  setZoom: (zoom: number) => void
  focused: boolean
  depth: number
  position: any
  content?: string | undefined
  modified: number
  color?: string | undefined
  bold?: boolean | undefined
  preserved?: boolean | undefined
  width: number
  height: number
  velocity: any
  force: any

  constructor(attrs: PointAttrs) {
    const { setStyle, setZoom, focused, depth, position, content, modified, color, bold, preserved } = attrs
    this.setStyle = setStyle
    this.setZoom = setZoom
    this.focused = Boolean(focused)
    this.depth = depth || 1
    this.position = position || new Vector(0, 0)
    this.content = content
    this.modified = modified || Date.now()
    this.color = color
    this.bold = bold
    this.preserved = preserved
    this.width = 0
    this.height = 0
    this.velocity = new Vector(0, 0)
    this.force = new Vector(0, 0)
  }

  setSize(width: number, height: number) {
    this.width = width
    this.height = height
  }

  getPosition() {
    return new Vector(this.position.x + this.width / 2, this.position.y + this.height / 2)
  }

  getColor() {
    return this.color || 'white'
  }

  getLineWidth() {
    return 1
  }

  applyForce(f: any) {
    if (isNaN(f.x)) f.x = 0
    if (isNaN(f.y)) f.y = 0
    this.force.add_m(f.x, f.y)
  }

  getLineToEllipse(p: Point) {
    const from_p = this.getPosition()
    const from_ell = this.getEllipse()
    const to_p = p.getPosition()
    const to_ell = p.getEllipse()
    const ang = from_p.angle(to_p)
    const from_intersect = from_ell.getPoint(ang + Math.PI, from_p)
    const to_intersect = to_ell.getPoint(ang, to_p)
    if (!(to_intersect && from_intersect)) return null
    return new Line(from_intersect, to_intersect)
  }

  getLineToNode(n: Point, displace = 0) {
    let whole_line: any
    if (displace) {
      const get_changed = (from: any, to: any) => {
        const orig = to.sub(from)
        const normal = orig.normalize()
        const orth = normal.rotate((3 * Math.PI) / 2.0)
        orth.mul_m(displace)
        const new_to = to.add(orth)
        const new_from = from.add(orth)
        return [new_from, new_to]
      }
      const changed = get_changed(this.getPosition(), n.getPosition())
      whole_line = new Line(changed[0], changed[1])
    } else {
      whole_line = new Line(this.getPosition(), n.getPosition())
    }
    const to_intersect = n.getBox().intersectLine(whole_line)
    const from_intersect = this.getBox().intersectLine(whole_line)
    if (!(to_intersect && from_intersect)) return null
    return new Line(from_intersect, to_intersect)
  }

  getLineToPoint(p: any) {
    const center = this.getPosition()
    const longP = p.sub(center).mul(10000).add(center)
    const whole_line = new Line(center, longP)
    const to_intersect = p
    const from_intersect = this.getBox().intersectLine(whole_line)
    if (!(to_intersect && from_intersect)) return null
    return new Line(from_intersect, to_intersect)
  }

  getEllipse() {
    const w = this.width / 2.0
    const h = this.height / 2.0
    let e = new Ellipse(w, h)
    const dia = Math.sqrt(w * w + h * h)
    const dia_ang = Math.atan(h / w)
    const rad = e.getRadius(dia_ang)
    const scalar = (dia / rad - 1) / 2 + 1
    e = e.scale(scalar)
    if (isNaN(e.a)) e.a = 0
    if (isNaN(e.b)) e.b = 0
    return e
  }

  getBox() {
    return new Box(new Vector(this.position.x, this.position.y), this.width, this.height)
  }

  centerTo(p: any) {
    this.position.x = p.x - this.width / 2
    this.position.y = p.y - this.height / 2
  }

  getArea() {
    return this.width * this.height
  }
}
